// The iframe side of the embed protocol (Frontend §4.3; Security §6.5): the handshake, the host origin, and the only
// two messages the frame accepts from its parent (`init`, `close`). Plain DOM, no React.
import { INIT_WAIT_MS, isPaMessage, PA_NS, PROTOCOL_VERSION, type FrameToHost } from '../../../embed/protocol'

/** Every iframe → host message, minus the envelope. */
export type FrameMessage = FrameToHost extends infer M ? (M extends unknown ? Omit<M, 'ns' | 'v'> : never) : never

export interface HostBridge {
  /** Resolves once the handshake is done, or after 5 s without a valid `init` (Frontend §4.4 step 1). */
  readonly settled: Promise<void>
  /** The host origin from the accepted `init`, or null (no handshake yet, or none within 5 s). */
  hostOrigin(): string | null
  /**
   * The embed-origin signal for #2 (Frontend §4.3, Security §6.5): the handshake origin, else
   * `location.ancestorOrigins[0]`, else the `document.referrer` origin. A weak signal only.
   */
  embedOrigin(): string | null
  /** Posts to the host origin only (never `'*'`); does nothing without a handshake. UI events only, never PHI. */
  post(message: FrameMessage): void
  /** The host asked to close (launcher X or `PFAppointments.close()`). Returns the unsubscribe. */
  onClose(listener: () => void): () => void
  destroy(): void
}

export interface HostBridgeOptions {
  /** The frame's window. */
  win: Window
  /** Its parent (`window.parent`); tests pass a stand-in window. */
  parent: Window
  /** The `#n=` nonce embed.js put in the iframe URL (`takeNonce`). */
  nonce: string | null
  initWaitMs?: number
}

/** Reads the `#n={nonce}` hash and removes it from the URL (`history.replaceState`: no history entry). */
export function takeNonce(win: Window): string | null {
  const match = /^#n=([A-Za-z0-9-]{8,64})$/.exec(win.location.hash)
  if (win.location.hash !== '') {
    win.history.replaceState(win.history.state, '', `${win.location.pathname}${win.location.search}`)
  }
  return match?.[1] ?? null
}

const originOf = (url: string): string | null => {
  try {
    const origin = new URL(url).origin
    return origin === 'null' ? null : origin
  } catch {
    return null
  }
}

/**
 * Accepts only the first `init` whose `event.source === parent` and whose nonce matches, and stores `event.origin`
 * as the host origin. After that, only `close` from the parent at that origin. Protocol `v` and `v - 1` are accepted.
 * Without a valid `init` within 5 s the frame still works but never posts.
 */
export function createHostBridge({ win, parent, nonce, initWaitMs = INIT_WAIT_MS }: HostBridgeOptions): HostBridge {
  let host: string | null = null
  let gaveUp = false
  const closeListeners = new Set<() => void>()
  let settle: () => void = () => undefined
  const settled = new Promise<void>((resolve) => {
    settle = resolve
  })
  const timer = setTimeout(() => {
    gaveUp = true
    settle()
  }, initWaitMs)

  const post = (message: FrameMessage) => {
    if (host === null) return
    parent.postMessage({ ns: PA_NS, v: PROTOCOL_VERSION, ...message }, host)
  }

  const onMessage = (event: MessageEvent) => {
    if (event.source !== parent) return
    const data: unknown = event.data
    if (!isPaMessage(data) || (data.v !== PROTOCOL_VERSION && data.v !== PROTOCOL_VERSION - 1)) return
    if (host === null) {
      if (gaveUp || data.type !== 'init' || nonce === null || data.nonce !== nonce) return
      if (originOf(event.origin) === null) return
      host = event.origin
      clearTimeout(timer)
      post({ type: 'ready', nonce })
      settle()
      return
    }
    if (event.origin !== host) return
    if (data.type === 'close') for (const listener of [...closeListeners]) listener()
  }
  win.addEventListener('message', onMessage)

  return {
    settled,
    hostOrigin: () => host,
    embedOrigin() {
      if (host !== null) return host
      const ancestors = win.location.ancestorOrigins as DOMStringList | undefined
      const first = ancestors && ancestors.length > 0 ? ancestors.item(0) : null
      if (first) return originOf(first)
      return win.document.referrer ? originOf(win.document.referrer) : null
    },
    post,
    onClose(listener) {
      closeListeners.add(listener)
      return () => {
        closeListeners.delete(listener)
      }
    },
    destroy() {
      clearTimeout(timer)
      win.removeEventListener('message', onMessage)
      closeListeners.clear()
    },
  }
}
